import { apiClient } from './api';

export interface PlatformMetrics {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  suspendedTenants: number;
  totalUsers: number;
  activeUsers: number;
  totalStores: number;
  totalRevenue: number;
  mrr: number;
  totalOrders: number;
  totalProducts: number;
}

export interface TimeSeriesData {
  date: string;
  value: number;
}

export interface TenantGrowthData {
  period: string;
  newTenants: number;
  churnedTenants: number;
  totalTenants: number;
}

export interface RevenueByPlan {
  plan: string;
  revenue: number;
  percentage: number;
}

export interface TopTenant {
  id: string;
  name: string;
  revenue: number;
  orders: number;
  users: number;
}

export interface PlatformAnalytics {
  metrics: PlatformMetrics;
  tenantGrowth: TenantGrowthData[];
  revenueTrend: TimeSeriesData[];
  userGrowth: TimeSeriesData[];
  revenueByPlan: RevenueByPlan[];
  topTenants: TopTenant[];
  apiUsage: TimeSeriesData[];
  errorRate: TimeSeriesData[];
}

export interface DateRangeParams {
  startDate?: string;
  endDate?: string;
  period?: 'day' | 'week' | 'month' | 'year';
}

export const analyticsService = {
  getPlatformAnalytics: async (params: DateRangeParams = {}): Promise<PlatformAnalytics> => {
    const response = await apiClient.get('/analytics/platform', { params });
    return response.data.data;
  },

  getTenantGrowth: async (params: DateRangeParams = {}): Promise<TenantGrowthData[]> => {
    const response = await apiClient.get('/analytics/tenant-growth', { params });
    return response.data.data;
  },

  getRevenueTrend: async (params: DateRangeParams = {}): Promise<TimeSeriesData[]> => {
    const response = await apiClient.get('/analytics/revenue-trend', { params });
    return response.data.data;
  },

  getUserGrowth: async (params: DateRangeParams = {}): Promise<TimeSeriesData[]> => {
    const response = await apiClient.get('/analytics/user-growth', { params });
    return response.data.data;
  },

  getApiUsage: async (params: DateRangeParams = {}): Promise<TimeSeriesData[]> => {
    const response = await apiClient.get('/analytics/api-usage', { params });
    return response.data.data;
  },

  getErrorRate: async (params: DateRangeParams = {}): Promise<TimeSeriesData[]> => {
    const response = await apiClient.get('/analytics/error-rate', { params });
    return response.data.data;
  },

  getRealtimeMetrics: async (): Promise<{
    activeUsers: number;
    apiRequestsPerMinute: number;
    ordersToday: number;
    revenueToday: number;
  }> => {
    const response = await apiClient.get('/analytics/realtime');
    return response.data.data;
  },
};

export default analyticsService;
